import React, { useReducer } from "react";

import {
  RateButton,
  Container,
  Input,
  MessageContainer,
  SaveButton,
  FeedbackText,
  ResolvedContainer,
  MarkAsResolvedButton,
  MarkAsUnresolvedButton,
} from "./styled";
import { Icon } from "../Icon";
import { useChatClass, useTheme } from "../../theme";
import { toLocaleDateString } from "../../utils";

/** - `positive` = thumbs up
 * - `negative` = thumbs down  */
export type FeedbackRating = "positive" | "negative";

/** Feedback data needed for UI */
export interface FeedbackData {
  /** Rating of thumbs up or down */
  rating?: FeedbackRating;
  /** Feedback left by chatbot user */
  message?: string;
  /** Date feedback was created */
  createdAt?: Date;
  /** Name of person (or "TOMIS") that created the feedback */
  createdBy?: string;
  /** TOMIS ID of user that created the feedback */
  createdByTomisId?: string;
  /** Email address of user that created the feedback. */
  createdByEmail?: string;
  /** Date feedback was resolved */
  resolvedAt?: Date;
  /** Name of person (or "TOMIS") that resolved the feedback */
  resolvedBy?: string;
  /** TOMIS ID of user that resolved the feedback */
  resolvedByTomisId?: string;
  /** If feedback has been resolved */
  resolved?: boolean;
}

export interface FeedbackProps {
  /** - view = show to users, don't allow modifications of any kind
   * - edit = allow users to leave feedback. Default mode for chatbot users.
   * - review = for TOMIS employees to mark feedback as "resolved" */
  mode: "view" | "edit" | "review" | "disabled";
  /** Feedback data will exist if chatbot user has interacted with the feedback,
   * either by rating or leaving feedback. */
  data?: FeedbackData;
  /** Callback function to save "rating" of thumbs up or down. Use `undefined`
    to clear the rating. */
  onSaveRating?: (type: FeedbackRating | undefined) => any;
  /** Callback function to save "feedback" left by chatbot user */
  onSaveFeedback?: (text: string) => any;
  /** Callback function to mark feedback as resolved,
   * if not provided, the "resolved" button will not be shown. */
  onSaveResolved?: (isResolved: boolean) => any;
}

interface MessageState {
  message: string;
  status: "idle" | "saving" | "success" | "error";
}

function messageReducer(
  state: MessageState,
  action: MessageAction,
): MessageState {
  switch (action.type) {
    case "SET_MESSAGE":
      return { ...state, message: action.payload };
    case "START_SAVING":
      return { ...state, status: "saving" };
    case "STOP_SAVING":
      return { ...state, status: "idle" };
    default:
      return state;
  }
}

type MessageAction =
  | { type: "SET_MESSAGE"; payload: string }
  | { type: "START_SAVING" }
  | { type: "STOP_SAVING" };

interface FeedbackComponent extends React.FC<FeedbackProps> {
  Container: typeof Container;
}

/** Collect feedback from chatbot user for a particular System Response, which
 * might include several System Messages.
 *
 * - User can rate the System Response as "positive" or "negative"
 * - User can leave feedback in a text input
 *
 * Initially this is only available for TOMIS users when testing the chatbot,
 * but we may decide to expose this functionality to all users in the future.
 */
const Feedback: FeedbackComponent = ({
  mode,
  data,
  onSaveRating,
  onSaveFeedback,
  onSaveResolved,
}) => {
  const theme = useTheme();
  const chatClass = useChatClass();
  const [state, dispatch] = useReducer(messageReducer, {
    message: data?.message || "",
    status: "idle",
  });

  /** Local state for the feedback message is not in sync w/ external state (api or other) */
  const hasUnsavedChanges = state.message != (data?.message || "");

  if (mode === "disabled") return null;

  if (mode === "view" && data && (data.rating || data.message)) {
    const resolvedText = createResolvedText(data);

    return (
      <Container
        className={`${chatClass("feedback")} ${chatClass("feedback-view")}`}
        $alignItems="center"
      >
        <RateButton $selected={data.rating === "positive"} $rating="positive">
          <Icon
            name="thumbs-up"
            size={20}
            color={
              data.rating === "positive"
                ? theme.colors.white
                : theme.colors.grayDarker
            }
          />
        </RateButton>
        <RateButton $selected={data.rating === "negative"} $rating="negative">
          <Icon
            name="thumbs-up"
            size={20}
            color={
              data.rating === "negative"
                ? theme.colors.white
                : theme.colors.grayDarker
            }
          />
        </RateButton>
        <MessageContainer>
          <FeedbackText>{data.message || ""}</FeedbackText>
          {data.resolved && (
            <ResolvedContainer>
              <Icon name="checkmark" size={14} color={theme.colors.green} />
              <FeedbackText>{resolvedText}</FeedbackText>
            </ResolvedContainer>
          )}
        </MessageContainer>
      </Container>
    );
  }

  // Don't show feedback UI if there is no data
  if (mode === "review" && data && (data.rating || data.message)) {
    const resolvedText = createResolvedText(data);

    return (
      <Container
        className={`${chatClass("feedback")} ${chatClass("feedback-review")}`}
      >
        <RateButton $selected={data.rating === "positive"} $rating="positive">
          <Icon
            name="thumbs-up"
            size={20}
            color={
              data.rating === "positive"
                ? theme.colors.white
                : theme.colors.grayDarker
            }
          />
        </RateButton>
        <RateButton $selected={data.rating === "negative"} $rating="negative">
          <Icon
            name="thumbs-up"
            size={20}
            color={
              data.rating === "negative"
                ? theme.colors.white
                : theme.colors.grayDarker
            }
          />
        </RateButton>
        <MessageContainer>
          <FeedbackText>{state.message}</FeedbackText>
          {data?.resolved ? (
            <ResolvedContainer>
              <Icon name="checkmark" size={14} color={theme.colors.green} />
              <FeedbackText>{resolvedText}</FeedbackText>
              <MarkAsUnresolvedButton onClick={() => onSaveResolved?.(false)}>
                <Icon
                  name="refresh"
                  size={12}
                  color={theme.colors.warn}
                  containerStyle={{ marginRight: theme.space[2] }}
                />
                undo
              </MarkAsUnresolvedButton>
            </ResolvedContainer>
          ) : (
            <MarkAsResolvedButton onClick={() => onSaveResolved?.(true)}>
              <Icon
                name="checkmark"
                size={14}
                color={theme.colors.green}
                containerStyle={{ marginRight: theme.space[2] }}
              />
              resolve
            </MarkAsResolvedButton>
          )}
        </MessageContainer>
      </Container>
    );
  }

  if (mode === "edit") {
    function handleKeyPress(event: React.KeyboardEvent<HTMLTextAreaElement>) {
      event.stopPropagation();
      const { shiftKey } = event;

      if (event.key !== "Enter") return;
      if (event.key === "Enter" && !shiftKey) {
        event.preventDefault();
        handleSaveFeedback();
      }
    }

    /** Save feedback & simulate async request with timeout */
    async function handleSaveFeedback(afterSave?: () => any) {
      if (hasUnsavedChanges) {
        dispatch({ type: "START_SAVING" });
        onSaveFeedback?.(state.message);
        // artificially delay the "saving" state to show the user
        setTimeout(() => {
          dispatch({ type: "STOP_SAVING" });
          afterSave?.();
        }, 400);
      }
    }

    return (
      <Container
        className={`${chatClass("feedback")} ${chatClass("feedback-edit")}`}
      >
        <RateButton
          $selected={data?.rating === "positive"}
          $rating="positive"
          onClick={() =>
            onSaveRating?.(data?.rating === "positive" ? undefined : "positive")
          }
        >
          <Icon
            name="thumbs-up"
            size={20}
            color={
              data?.rating === "positive"
                ? theme.colors.white
                : theme.colors.grayDark
            }
          />
        </RateButton>
        <RateButton
          $selected={data?.rating === "negative"}
          $rating="negative"
          onClick={() =>
            onSaveRating?.(data?.rating === "negative" ? undefined : "negative")
          }
        >
          <Icon
            name="thumbs-up"
            size={20}
            color={
              data?.rating === "negative"
                ? theme.colors.white
                : theme.colors.grayDark
            }
          />
        </RateButton>
        <MessageContainer>
          <Input
            $hasUnsavedChanges={hasUnsavedChanges}
            value={state.message}
            onKeyDown={handleKeyPress}
            onChange={(e) =>
              dispatch({ type: "SET_MESSAGE", payload: e.target.value })
            }
            placeholder="Leave feedback..."
          />

          <SaveButton
            onMouseDown={(e) => {
              e.preventDefault();
              handleSaveFeedback();
            }}
          >
            <Icon
              name={state.status === "saving" ? "refresh" : "checkmark"}
              size={18}
              color={
                hasUnsavedChanges || state.status === "saving"
                  ? theme.colors.green
                  : theme.colors.grayMed
              }
              containerStyle={
                state.status === "saving"
                  ? { animation: "spin 1s linear infinite" }
                  : undefined
              }
            />
          </SaveButton>
        </MessageContainer>
      </Container>
    );
  }

  // unexpected, return nothing
  return null;
};

function createResolvedText(data: FeedbackData) {
  let resolvedText = data.resolved ? "Resolved" : "";

  if (data.resolvedBy) {
    resolvedText += ` by ${data.resolvedBy}`;
  }

  if (data.resolvedAt) {
    resolvedText += ` on ${toLocaleDateString({
      date: data.resolvedAt,
      options: {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      },
    })}`;
  }

  return resolvedText;
}

Feedback.Container = Container;

export { Feedback };
